import { useState, useRef, useCallback } from 'react';

/**
 * Manages local camera state independently of peer connections.
 *
 * onTrackReady(track, stream) — caller adds track to all active peer connections and renegotiates.
 * onTrackStopped() — caller removes video senders from all peer connections and renegotiates.
 */
export function useCamera({ onTrackReady, onTrackStopped }) {
  const [cameraOn, setCameraOn] = useState(false);
  const localVideoStreamRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (localVideoStreamRef.current) {
      localVideoStreamRef.current.getTracks().forEach((t) => t.stop());
      localVideoStreamRef.current = null;
    }
  }, []);

  const toggleCamera = useCallback(async () => {
    if (cameraOn) {
      stopCamera();
      await onTrackStopped();
      setCameraOn(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        localVideoStreamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        await onTrackReady(track, stream);
        setCameraOn(true);
      } catch (err) {
        console.error('[useCamera] Camera access denied:', err);
      }
    }
  }, [cameraOn, onTrackReady, onTrackStopped, stopCamera]);

  return { cameraOn, setCameraOn, toggleCamera, localVideoStreamRef, stopCamera };
}
